import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../Api/ApiMethods';

const BankDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedBank, refreshData }) => {
    const initialBankData = {
        id: '',
        bankName: '',
        bankCode: '',
        branchName: '',
        branchCode: '',
        iban: '',
        swiftBicCode: '',
        currency: '',
    };

    const [bankData, setBankData] = useState(initialBankData);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedBank) {
                setBankData({ ...initialBankData, ...selectedBank });
            } else {
                resetForm();
            }
        }
    }, [mode, selectedBank, isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let processedValue = value;

        const fieldsToUppercase = ['bankName', 'bankCode', 'branchName', 'branchCode'];

        if (fieldsToUppercase.includes(name) && typeof value === 'string') {
            processedValue = value.toLocaleUpperCase('tr-TR');
        }

        setBankData({ ...bankData, [name]: processedValue });

        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        const ibanRegex = /^TR\d{2}\s?(\d{4}\s?){5}\d{2}$/;
        const swiftBicRegex = /^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;

        if (!bankData.bankName.trim()) {
            validationErrors.bankName = 'Banka Adı zorunludur.';
        }

        if (!bankData.branchName.trim()) {
            validationErrors.branchName = 'Şube Adı zorunludur.';
        }

        if (!bankData.iban.trim()) {
            validationErrors.iban = 'IBAN zorunludur.';
        } else if (!ibanRegex.test(bankData.iban.replace(/\s/g, ''))) {
            validationErrors.iban = 'Geçerli bir TR IBAN formatı giriniz.';
        }

        if (!bankData.currency) {
            validationErrors.currency = 'Para birimi zorunludur.';
        }

        if (bankData.swiftBicCode?.trim() && !swiftBicRegex.test(bankData.swiftBicCode)) {
            validationErrors.swiftBicCode = 'Geçerli bir SWIFT/BIC kodu giriniz (örn: AKBKTRISXXX)';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setBankData(initialBankData);
        setErrors({});
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleOperation = async () => {
        if (!validateForm()) {
            toast.error("Lütfen formdaki hataları düzeltin.");
            return;
        }

        const dataToSend = { ...bankData };

        await handleAddModifyOperation({
            apiEndpoint,
            selectedItem: mode === 'modify' ? selectedBank : null,
            mode,
            data: dataToSend,
            refreshDataCallback: refreshData,
            componentName: 'Banka',
        });

        handleCancel();
    };

    const getModalTitle = () => {
        return mode === 'add' ? 'Banka Ekle' : 'Banka Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel}>
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="bankName">Banka Adı <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="bankName"
                            name="bankName"
                            value={bankData.bankName}
                            onChange={handleInputChange}
                            placeholder="Banka Adı"
                            invalid={!!errors.bankName}
                        />
                        <FormFeedback>
                            {errors.bankName}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="bankCode">Banka Kodu</Label>
                        <Input
                            type="text"
                            id="bankCode"
                            name="bankCode"
                            value={bankData.bankCode}
                            onChange={handleInputChange}
                            invalid={!!errors.bankCode}
                        />
                        <FormFeedback>
                            {errors.bankCode}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="branchName">Şube Adı <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="branchName"
                            name="branchName"
                            value={bankData.branchName}
                            onChange={handleInputChange}
                            placeholder="Şube Adı"
                            invalid={!!errors.branchName}
                        />
                        <FormFeedback>
                            {errors.branchName}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="branchCode">Şube Kodu</Label>
                        <Input
                            type="text"
                            id="branchCode"
                            name="branchCode"
                            value={bankData.branchCode}
                            onChange={handleInputChange}
                            invalid={!!errors.branchCode}
                        />
                        <FormFeedback>
                            {errors.branchCode}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban">IBAN <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="iban"
                            name="iban"
                            value={bankData.iban}
                            onChange={(e) => {
                                let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                if (value.startsWith('TR') && value.length > 26) {
                                    value = value.slice(0, 26);
                                } else if (!value.startsWith('TR') && value.length > 24) {
                                    value = value.slice(0, 24);
                                }
                                if (value.startsWith('TR')) {
                                    value = value.replace(/(.{4})/g, '$1 ').trim();
                                }
                                setBankData(prev => ({ ...prev, iban: value }));
                                if (errors.iban) setErrors(prev => ({ ...prev, iban: '' }));
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            invalid={!!errors.iban}
                        />
                        <FormFeedback>
                            {errors.iban}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="currency">Para Birimi <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="currency"
                            name="currency"
                            value={bankData.currency}
                            onChange={handleInputChange}
                            invalid={!!errors.currency}
                        >
                            <option value="">Seçiniz</option>
                            <option value="USD">USD (Amerikan Doları)</option>
                            <option value="EUR">EUR (Euro)</option>
                            <option value="TRY">TRY (Türk Lirası)</option>
                            <option value="GBP">GBP (İngiliz Sterlini)</option>
                        </Input>
                        <FormFeedback>
                            {errors.currency}
                        </FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="swiftBicCode">SWIFT/BIC Kodu</Label>
                        <Input
                            type="text"
                            id="swiftBicCode"
                            name="swiftBicCode"
                            value={bankData.swiftBicCode}
                            onChange={(e) => {
                                let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                if (value.length > 11) {
                                    value = value.slice(0, 11);
                                }
                                setBankData(prev => ({ ...prev, swiftBicCode: value }));
                                if (errors.swiftBicCode) setErrors(prev => ({ ...prev, swiftBicCode: '' }));
                            }}
                            placeholder="AKBKTRISXXX"
                            invalid={!!errors.swiftBicCode}
                        />
                        <FormFeedback>
                            {errors.swiftBicCode}
                        </FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-primary" onClick={handleOperation}>
                    {getButtonLabel()}
                </button>
                <button className="btn btn-secondary" onClick={handleCancel}>
                    İptal
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default BankDataModal;
